import { Avatar, Box, CardHeader, Fab, IconButton, InputAdornment, OutlinedInput, Tooltip, Typography, useTheme } from "@mui/material";
import { Card, CardActions, CardContent, styled } from "@mui/material";
import { CloseCircle, Edit2, Messages1, ProfileTick } from "iconsax-react";
import ChatList from "./ChatList";
import { useEffect, useState } from "react";
// import ListParticipants from "./ListParticipants";
import { CallStatus } from "types/auth";
import useSession from "hooks/call-queue/useSession";
import useApi from "hooks/useApi";
import useFirebase from "hooks/useFirebase";
// import SupportReview from "./SupportReview";
import ChatInput from "./ChatInput";
import { useNavigate } from "react-router";


const ChatCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
    boxShadow: 'none',
    backgroundColor: theme.palette.background.paper,
    border: 'none'
}));



interface ChatWindowProps {
    onEndingSession?: (isEnding: boolean) => void;
}

const ChatWindow = ({ onEndingSession }: ChatWindowProps) => {
    const theme = useTheme();
    const { session, messages } = useSession();
    const { updateSessionUserName, showToast, user, endSession } = useApi();
    const { updateFromName, markMessagesAsRead, addCallHistoryEntry, updateCallStatus} = useFirebase();
    // const [open, setOpen] = useState(false);
    const [editUserName, setEditUsername] = useState(false);
    const [userNameConst, setUserNameConst] = useState<string | null>(null);
    const [userName, setUserName] = useState<string | null>(null);

    const navigate = useNavigate();

    // Function to get user initials
    const getUserInitials = (name: string | null | undefined) => {
        if (!name?.trim()) return 'U';
        const words = name.trim().split(/\s+/).slice(0, 2);
        if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
        return (words[0][0] + words[1][0]).toUpperCase();
    };

    // const handleClickOpen = () => {
    //     setOpen(true);
    // };

    // const handleClose = () => {
    //     setOpen(false);
    // };

    useEffect(() => {
        if(session && session?.fromName){
            setUserName(session?.fromName!);
            setUserNameConst(session?.fromName!);
        }
    }, [session])

    // useEffect(()=>{
    //     console.log('hiiii empty return....')
    //     return ()=>{
    //         showCallNotification();
    //     }
    // },[])

    const markMessagesRead = () => {
        if(!session || !user) return;
        markMessagesAsRead(session?.transaction!, user!.uid!)
    }

    useEffect(() => {
        markMessagesRead();
    }, [messages])

    // NOTE: the ping/toast for incoming messages lives in the globally-mounted
    // CallMessageAlert, so it still fires when the call is minimised or the
    // agent is on another page. Keeping a copy here would double-alert.

    // Handle redirection when chat ends
    useEffect(() => {
        if (session?.status === CallStatus.COMPLETED && session?.sessionType === "CHAT") {
            showToast('Chat session has ended. Redirecting to home...', 'info');
            // Show the "Chat has ended" message for 3 seconds, then redirect
            const timer = setTimeout(() => {
                navigate('/');
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [session?.status, session?.sessionType, navigate])

    const onClickChangeUsername = async () => {
        try{
            let response = await updateSessionUserName(session?.transaction!, userName!);
            let { success, message } = response;
            if(success){
                showToast('User Name has been updated', 'success');
                updateFromName(session?.transaction!, userName!);
                setEditUsername(false)
            }else{
                showToast(message, 'error');
            }
        }catch(error: any){
            showToast(error.message, 'error');
        }
    }

    const disconnectCall = async () => {
        onEndingSession?.(true);

        if(session?.supportAccepted != user.uid){
            addCallHistoryEntry(session!.transaction!, {
                status: CallStatus.SUPPORT_LEFT,
                datetime: new Date().toISOString(),
                user: user!.uid
            });

            showToast('Exiting the session. Thank you!', 'success');
            navigate('/')
            return;
        }

        try{
            let response = await endSession(session?.transaction!);
            let { success, message } = response;

            if(success){
                addCallHistoryEntry(session!.transaction!, {
                    status: CallStatus.SUPPORT_LEFT,
                    datetime: new Date().toISOString(),
                    user: user!.uid
                });

                addCallHistoryEntry(session!.transaction!, {
                    status: CallStatus.COMPLETED,
                    datetime: new Date().toISOString(),
                    user: user!.uid
                });

                updateCallStatus(session!.transaction!, CallStatus.COMPLETED);
                
                showToast('Session has been ended', 'success');
                navigate('/');
            }else{
                showToast(message, 'error');
            }
        }catch(error: any){
            showToast(error.message, 'error');
        }finally{
            onEndingSession?.(false);
        }
        
    }

    
    return (
        <>
   
        <ChatCard sx={{ display: 'flex', flexDirection: 'column', padding: 0, position: 'relative', height: '100%' }}>
            {
                (session && session?.status != CallStatus.IN_PROGRESS && session?.status != CallStatus.COMPLETED && session?.status != CallStatus.USER_LEFT) && (
                    <Box sx={{
                        height: '100%',
                        width: '100%',
                        background: '#fff',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <Typography variant="h6" fontWeight={'bold'}>Joining Room...</Typography>
                    </Box>
                )
            }
            
            {/* Show "Chat has ended" message when session is completed */}
            {
                (session && session?.status === CallStatus.COMPLETED && session?.sessionType === "CHAT") && (
                    <Box sx={{
                        height: '100%',
                        width: '100%',
                        background: '#fff',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        gap: 2
                    }}>
                        <Messages1 size="96" color={theme.palette.primary[200]} />
                        <Typography variant="h5" fontWeight={'bold'} color="text.primary">
                            Chat has ended
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            The chat session has been completed.
                        </Typography>
                    </Box>
                )
            }
            
            {
                (session && (session?.status == CallStatus.IN_PROGRESS || session?.status == CallStatus.USER_LEFT)) && (
                    <>
                        <CardHeader sx={{
                            padding: 2,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            borderRadius: 0
                        }} title={
                            <Box sx={{ display: 'flex' }}>
                                <Avatar sx={{ 
                                    bgcolor: 'primary.main', 
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '14px'
                                }}>
                                    {getUserInitials(userName || session?.fromName)}
                                </Avatar>
                                <Box sx={{ px: 1, flex: 1 }}>
                                    
                                    {!editUserName && (
                                        <Tooltip title={'Click to Update Name'} placement="top-start">
                                            <Box
                                                sx={{ display: 'flex', gap: 1, cursor: 'pointer' }}
                                                onClick={() => {
                                                    setEditUsername(true)
                                                }}
                                            >
                                                <Typography
                                                    color={"#fff"}
                                                    lineHeight={1}
                                                    variant="body1"
                                                    fontWeight={'bold'}
                                                >
                                                    {userName ? userName : 'Loading...'}
                                                </Typography>
                                                <Edit2 size={12} color={ '#fff' } />
                                            </Box>
                                        </Tooltip>
                                    )}
                                    {!editUserName && <Typography color={"#fff"} lineHeight={1} variant="caption">{session ? session?.key : 'Loading...'}</Typography>}
                                    {
                                        editUserName && (
                                            <OutlinedInput
                                                id="chat-input"
                                                defaultValue={userName}
                                                onInput={(e: any) => {
                                                    console.log(e.target.value, "eeeeeeeeeeeee");
                                                    
                                                    setUserName(e.target.value);
                                                }}
                                                endAdornment={
                                                    <InputAdornment position="end" sx={{ mr: -0.5 }}>
                                                        <IconButton
                                                            onClick={() => {
                                                                setEditUsername(false)
                                                                setUserName(userNameConst);
                                                            }}
                                                            color={'error'}
                                                            sx={{ borderRadius: '50%' }}
                                                        >
                                                            <CloseCircle size={16} color={ theme.palette.error.main } />
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={() => { onClickChangeUsername() }}
                                                            color={'primary'}
                                                            sx={{ borderRadius: '50%' }}
                                                        >
                                                            <ProfileTick size={16} color={ theme.palette.primary.main } />
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                                aria-describedby="header-search-text"
                                                inputProps={{
                                                    'aria-label': 'weight'
                                                }}
                                                placeholder="Type a message"
                                                fullWidth
                                                sx={{ '& .MuiOutlinedInput-input': { p: 1.5 }, pr: 1, borderRadius: '30px', background: '#fff' }}
                                            />
                                        )
                                    }
                                </Box>
                                {/* <Fab
                                    color="primary"
                                    size="small"
                                    onClick={() => {
                                        handleClickOpen();
                                    }}
                                >
                                    <Profile2User
                                        size="32"
                                        color={"#fff"}
                                    />
                                </Fab> */}
                                {/* Only show End Chat button for CHAT sessions */}
                                {session?.sessionType == "CHAT" && 
                                    <Fab 
                                        color="error" 
                                        size="small"  
                                        onClick={() => {  disconnectCall(); }}
                                        sx={{
                                            backgroundColor: 'error.main',
                                            '&:hover': {
                                                backgroundColor: 'error.dark',
                                            }
                                        }}
                                    >
                                        <CloseCircle size="32" color={"#fff"}  />
                                    </Fab>
                                }
                            </Box>
                        } />
                        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: 0.5, position: 'relative' }}>
                            {
                                messages.length == 0 && (
                                    <Box sx={{
                                        height: '100%',
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        flexDirection: 'column'
                                    }}>

                                        <Messages1 size="96" color={theme.palette.primary[100]} />

                                    </Box>
                                )
                            }
                            {
                                messages.length > 0 && (
                                    <ChatList />
                                )
                            }
                        </CardContent>
                        
                        <CardActions sx={{ 
                            background: theme.palette.grey[50], 
                            p: 2,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            borderRadius: 0
                        }}>
                            <ChatInput />
                        </CardActions>
                    
                    </>
                )
            }
            {/* { open && <ListParticipants {...{ handleClose }} /> } */}
        </ChatCard>
        </>
    )
}

export default ChatWindow;

